import React, { useState, useEffect } from 'react';
import { 
  Search, RefreshCw, Eye, Filter, ChevronUp, ChevronDown,
  ArrowUpDown, Users, Crown, CheckCircle, XCircle
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const AdminUsers = ({ onViewUser }) => {
  const { apiRequest } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('active'); // active, deleted, all
  const [planFilter, setPlanFilter] = useState('all'); // all, free, premium
  const [onboardingFilter, setOnboardingFilter] = useState('all'); // all, completed, incomplete

  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const loadUsers = async () => {
    try {
      // Use admin API endpoint to bypass RLS
      const response = await apiRequest('/api/admin/users?status=all&limit=100');
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      
      // Process users with display names - API returns 'users' array
      const usersWithCounts = (data.users || []).map(user => ({
        ...user,
        subscriptionCount: user.active_subscriptions || 0,
        displayName: user.first_name 
          ? `${user.first_name} ${user.last_name || ''}`.trim()
          : user.full_name || ''
      }));

      setUsers(usersWithCounts);
      setLoading(false);
    } catch (error) {
      console.error('Error loading users:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...users];

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter(u => !u.deleted_at);
    } else if (statusFilter === 'deleted') {
      result = result.filter(u => u.deleted_at);
    }

    // Plan filter
    if (planFilter === 'free') {
      result = result.filter(u => u.plan_type !== 'premium');
    } else if (planFilter === 'premium') {
      result = result.filter(u => u.plan_type === 'premium');
    }

    // Onboarding filter
    if (onboardingFilter === 'completed') {
      result = result.filter(u => u.onboarding_completed);
    } else if (onboardingFilter === 'incomplete') {
      result = result.filter(u => !u.onboarding_completed);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => 
        u.email?.toLowerCase().includes(term) ||
        u.first_name?.toLowerCase().includes(term) ||
        u.last_name?.toLowerCase().includes(term) ||
        u.full_name?.toLowerCase().includes(term) ||
        u.zip_code?.includes(term)
      );
    }

    // Sorting
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle null values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Handle dates
      if (sortField === 'created_at' || sortField === 'last_query_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      // Handle numbers
      if (sortField === 'queries_total' || sortField === 'queries_used_month' || sortField === 'subscriptionCount') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      // Handle strings
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    setFilteredUsers(result);
  }, [users, searchTerm, statusFilter, planFilter, onboardingFilter, sortField, sortDirection]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadUsers();
    setRefreshing(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortableHeader = ({ field, label, className = '' }) => (
    <th 
      className={`px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-50" />
        )}
      </div>
    </th>
  );

  const FilterButton = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded text-sm transition-colors ${
        active 
          ? 'bg-purple-600 text-white' 
          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
      }`}
    >
      {children}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-gray-400">{filteredUsers.length} total users</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search and Filter Toggle */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by email, name, or zip..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showFilters 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
          <div>
            <p className="text-sm text-gray-400 mb-2">Status</p>
            <div className="flex gap-2">
              <FilterButton active={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>
                Active
              </FilterButton>
              <FilterButton active={statusFilter === 'deleted'} onClick={() => setStatusFilter('deleted')}>
                Deleted
              </FilterButton>
              <FilterButton active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
                All
              </FilterButton>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-400 mb-2">Plan</p>
            <div className="flex gap-2">
              <FilterButton active={planFilter === 'all'} onClick={() => setPlanFilter('all')}>
                All Plans
              </FilterButton>
              <FilterButton active={planFilter === 'free'} onClick={() => setPlanFilter('free')}>
                Free
              </FilterButton>
              <FilterButton active={planFilter === 'premium'} onClick={() => setPlanFilter('premium')}>
                Premium
              </FilterButton>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-400 mb-2">Onboarding</p>
            <div className="flex gap-2">
              <FilterButton active={onboardingFilter === 'all'} onClick={() => setOnboardingFilter('all')}>
                All
              </FilterButton>
              <FilterButton active={onboardingFilter === 'completed'} onClick={() => setOnboardingFilter('completed')}>
                Completed
              </FilterButton>
              <FilterButton active={onboardingFilter === 'incomplete'} onClick={() => setOnboardingFilter('incomplete')}>
                Incomplete
              </FilterButton>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <SortableHeader field="email" label="USER" />
                <SortableHeader field="first_name" label="NAME" />
                <SortableHeader field="plan_type" label="PLAN" />
                <SortableHeader field="onboarding_completed" label="STATUS" />
                <SortableHeader field="subscriptionCount" label="SUBS" />
                <SortableHeader field="queries_total" label="QUERIES" />
                <SortableHeader field="last_active_at" label="LAST ACTIVE" />
                <SortableHeader field="created_at" label="JOINED" />
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-medium">
                        {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                      </div>
                      <div>
                        {user.displayName && (
                          <p className="text-white font-medium">{user.displayName}</p>
                        )}
                        <p className={`${user.displayName ? 'text-sm text-gray-400' : 'text-white font-medium'}`}>
                          {user.email}
                        </p>
                        {user.zip_code && (
                          <p className="text-xs text-gray-500">{user.zip_code}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-300">
                      {user.displayName || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded ${
                      user.plan_type === 'premium' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-gray-600 text-gray-300'
                    }`}>
                      {user.plan_type || 'free'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {user.onboarding_completed ? (
                      <span className="flex items-center gap-1 text-green-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Onboarded
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-yellow-400 text-sm">
                        <XCircle className="w-4 h-4" />
                        Incomplete
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {user.subscriptionCount}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-300">
                      {user.queries_total || 0}
                      <span className="text-xs text-gray-500 ml-1">
                        ({user.queries_used_month || 0} this mo)
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-sm">
                    {user.last_active_at 
                      ? new Date(user.last_active_at).toLocaleDateString()
                      : '—'
                    }
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onViewUser(user.id)}
                      className="flex items-center gap-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded transition-colors ml-auto"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No users found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;